import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  Moon,
  Sun,
  LogOut,
  User as UserIcon,
  Search,
  ShoppingCart,
  ChevronDown,
  Settings,
  ShoppingBag,
  X,
  Package,
  Users,
  FileText,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { useThemeStore } from '@/store/themeStore';
import { useAuthStore } from '@/store/authStore';
import { useSettingsStore } from '@/store/settingsStore';
import { useSidebarStore } from '@/store/sidebarStore';
import { ROUTES } from '@/lib/config/routes';
import { useCartStore } from '@/store/cartStore';
import { parkCartForRecovery } from '@/lib/utils/session';
import { useDashboardSummary } from '@/features/dashboard/hooks/useDashboardSummary';
import { formatCurrency } from '@/lib/utils/format';
import { httpClient } from '@/lib/api/client';
import { ENDPOINTS } from '@/lib/api/endpoints';
import { cn } from '@/lib/utils/cn';
import { env } from '@/lib/config/env';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import type { Paginated } from '@/types/common';
import type { Product, Customer, Sale } from '@/types/domain';

interface TopbarProps {
  onMenuClick: () => void;
}

export function Topbar({ onMenuClick }: TopbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const resolved = useThemeStore((s) => s.resolved);
  const toggle = useThemeStore((s) => s.toggle);
  const cartCount = useCartStore((s) => s.items.reduce((n, i) => n + i.quantity, 0));
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggleCollapsed = useSidebarStore((s) => s.toggle);
  const tenantName = useAuthStore((s) => s.tenantName);
  const storeName = useSettingsStore((s) => s.settings.storeName);
  const orgName = tenantName ?? storeName;

  const { data: dashboard } = useDashboardSummary();

  const initials = user?.fullName
    ?.split(' ')
    .map((p) => p[0])
    .slice(0, 1)
    .join('')
    .toUpperCase();

  const onLogout = () => {
    parkCartForRecovery();
    clearSession();
    useCartStore.getState().clear();
    navigate(ROUTES.login, { replace: true });
  };

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') setSearchOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-border bg-surface-elevated/80 px-3 backdrop-blur-xl lg:px-5">
        <button
          type="button"
          className="rounded-lg p-1.5 text-content-muted transition hover:bg-surface-muted hover:text-content lg:hidden"
          onClick={onMenuClick}
        >
          <Menu className="h-5 w-5" />
        </button>

        <div className="hidden items-center gap-3 lg:flex">
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="shrink-0 rounded-lg p-1.5 text-content-muted transition hover:bg-surface-muted hover:text-content"
          >
            {collapsed ? (
              <PanelLeftOpen className="h-[18px] w-[18px]" />
            ) : (
              <PanelLeftClose className="h-[18px] w-[18px]" />
            )}
          </button>
          <span className="hidden max-w-48 truncate rounded-full bg-brand-50 px-4 py-2 text-xs font-semibold text-brand-700 dark:bg-brand-900/20 dark:text-brand-300 lg:inline">
            {orgName}
          </span>
          <div className="flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 dark:bg-emerald-900/20">
            <ShoppingBag className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
              {dashboard
                ? `${dashboard.todayOrders} sales · ${formatCurrency(dashboard.todayRevenue)}`
                : 'No sales today'}
            </span>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3">
          <NotificationBell />

          <button
            onClick={() => setSearchOpen(true)}
            className="rounded-lg p-2 text-content-muted transition hover:text-content"
          >
            <Search className="h-4 w-4" />
          </button>

          <button
            onClick={() => navigate(ROUTES.pos)}
            title="Open Point of Sale"
            className="flex items-center gap-1.5 rounded-full bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-700"
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Sell</span>
            {cartCount > 0 && (
              <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold text-brand-700">
                {cartCount}
              </span>
            )}
          </button>

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className={cn(
                'flex items-center gap-2 rounded-lg px-2 py-1.5 transition',
                menuOpen ? 'bg-surface-muted' : 'hover:bg-surface-muted',
              )}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-xs font-bold text-white shadow-sm">
                {initials ?? <UserIcon className="h-4 w-4" />}
              </div>
              <div className="hidden text-left leading-tight md:block">
                <p className="text-[9px] capitalize tracking-wider text-content-subtle">
                  {user?.role?.replace('_', ' ')}
                </p>
                <p className="text-xs font-semibold text-content">{user?.fullName}</p>
              </div>
              <ChevronDown
                className={cn(
                  'hidden h-3.5 w-3.5 text-content-subtle transition-transform md:block',
                  menuOpen && 'rotate-180',
                )}
              />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-56 overflow-hidden rounded-xl border border-border bg-surface-elevated p-1.5 shadow-lg animate-fade-in">
                <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-content-subtle">
                  Account
                </p>
                <div className="border-b border-border px-3 py-2 mb-1">
                  <p className="text-sm font-semibold text-content">{user?.fullName}</p>
                  <p className="text-xs capitalize text-content-muted">{user?.role}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    toggle();
                    setMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-content transition hover:bg-surface-muted"
                >
                  {resolved === 'dark' ? (
                    <Sun className="h-4 w-4 text-content-muted" />
                  ) : (
                    <Moon className="h-4 w-4 text-content-muted" />
                  )}
                  {resolved === 'dark' ? 'Light mode' : 'Dark mode'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate(ROUTES.account);
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-content transition hover:bg-surface-muted"
                >
                  <UserIcon className="h-4 w-4 text-content-muted" />
                  Account
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate(ROUTES.settings);
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-content transition hover:bg-surface-muted"
                >
                  <Settings className="h-4 w-4 text-content-muted" />
                  Settings
                </button>
                <div className="my-1 border-t border-border" />
                <button
                  type="button"
                  onClick={onLogout}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
                <div className="mt-1 border-t border-border px-3 pb-1 pt-2 text-center text-[10px] text-content-subtle">
                  {env.appName} · v{env.appVersion}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {searchOpen && <SearchDialog onClose={() => setSearchOpen(false)} />}
    </>
  );
}

interface SearchResult {
  id: string;
  label: string;
  sublabel: string;
  icon: React.ComponentType<{ className?: string }>;
  route: string;
  category: string;
}

function SearchDialog({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const navigate = useNavigate();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const pages: SearchResult[] = [
    {
      id: 'p-dashboard',
      label: 'Dashboard',
      sublabel: 'Overview & analytics',
      icon: FileText,
      route: ROUTES.dashboard,
      category: 'Pages',
    },
    {
      id: 'p-pos',
      label: 'Point of Sale',
      sublabel: 'Create a sale',
      icon: ShoppingCart,
      route: ROUTES.pos,
      category: 'Pages',
    },
    {
      id: 'p-products',
      label: 'Products',
      sublabel: 'Manage catalog',
      icon: Package,
      route: ROUTES.products,
      category: 'Pages',
    },
    {
      id: 'p-sales',
      label: 'Sales',
      sublabel: 'Transaction history',
      icon: ShoppingBag,
      route: ROUTES.sales,
      category: 'Pages',
    },
    {
      id: 'p-customers',
      label: 'Customers',
      sublabel: 'Customer directory',
      icon: Users,
      route: ROUTES.customers,
      category: 'Pages',
    },
    {
      id: 'p-inventory',
      label: 'Inventory',
      sublabel: 'Stock levels',
      icon: Package,
      route: ROUTES.inventory,
      category: 'Pages',
    },
    {
      id: 'p-reports',
      label: 'Reports',
      sublabel: 'Business reports',
      icon: FileText,
      route: ROUTES.reports,
      category: 'Pages',
    },
    {
      id: 'p-settings',
      label: 'Settings',
      sublabel: 'Store configuration',
      icon: Settings,
      route: ROUTES.settings,
      category: 'Pages',
    },
  ];

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const items: SearchResult[] = [];

      try {
        const [prodRes, custRes, saleRes] = await Promise.allSettled([
          httpClient.get<Paginated<Product>>(ENDPOINTS.products.list, {
            params: { search: query, pageSize: 5 },
          }),
          httpClient.get<Paginated<Customer>>(ENDPOINTS.customers.list, {
            params: { search: query, pageSize: 5 },
          }),
          httpClient.get<Paginated<Sale>>(ENDPOINTS.sales.list, {
            params: { search: query, pageSize: 5 },
          }),
        ]);

        if (prodRes.status === 'fulfilled') {
          const prods = prodRes.value?.data ?? [];
          prods.forEach((p) =>
            items.push({
              id: `prod-${p.id}`,
              label: p.name,
              sublabel: `SKU: ${p.sku} · ${formatCurrency(p.price)}`,
              icon: Package,
              route: ROUTES.products,
              category: 'Products',
            }),
          );
        }
        if (custRes.status === 'fulfilled') {
          const custs = custRes.value?.data ?? [];
          custs.forEach((c) =>
            items.push({
              id: `cust-${c.id}`,
              label: c.fullName,
              sublabel: c.email || c.phone || 'Customer',
              icon: Users,
              route: ROUTES.customerDetail(c.id),
              category: 'Customers',
            }),
          );
        }
        if (saleRes.status === 'fulfilled') {
          const sales = saleRes.value?.data ?? [];
          sales.forEach((s) =>
            items.push({
              id: `sale-${s.id}`,
              label: s.invoiceNumber,
              sublabel: `${formatCurrency(s.total)} · ${s.status}`,
              icon: ShoppingBag,
              route: ROUTES.saleDetail(s.id),
              category: 'Sales',
            }),
          );
        }
      } catch {}

      setResults(items);
      setLoading(false);
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const filteredPages = query
    ? pages.filter((p) => p.label.toLowerCase().includes(query.toLowerCase()))
    : pages;

  const allResults = [...filteredPages, ...results];
  const grouped = allResults.reduce<Record<string, SearchResult[]>>((acc, r) => {
    (acc[r.category] ??= []).push(r);
    return acc;
  }, {});

  const go = useCallback(
    (route: string) => {
      navigate(route);
      onClose();
    },
    [navigate, onClose],
  );

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[15vh] backdrop-blur-sm animate-fade-in"
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-surface-elevated shadow-2xl animate-slide-up">
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          {loading ? (
            <div className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          ) : (
            <Search className="h-5 w-5 shrink-0 text-content-subtle" />
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products, customers, invoices, pages..."
            className="h-full w-full bg-transparent text-sm text-content outline-none placeholder:text-content-subtle"
          />
          <button
            onClick={onClose}
            className="shrink-0 rounded-lg p-1 text-content-subtle transition hover:bg-surface-muted hover:text-content"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-80 overflow-y-auto p-2">
          {query && !loading && allResults.length === 0 ? (
            <div className="py-10 text-center">
              <Search className="mx-auto h-8 w-8 text-content-subtle" />
              <p className="mt-2 text-sm text-content-muted">No results for "{query}"</p>
              <p className="text-xs text-content-subtle">Try a different search term</p>
            </div>
          ) : (
            Object.entries(grouped).map(([category, items]) => (
              <div key={category} className="mb-1">
                <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-content-subtle">
                  {category}
                </p>
                {items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => go(item.route)}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition hover:bg-surface-muted"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-muted">
                      <item.icon className="h-4 w-4 text-content-muted" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-content">{item.label}</p>
                      <p className="truncate text-xs text-content-muted">{item.sublabel}</p>
                    </div>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border px-4 py-2.5 text-[10px] text-content-subtle">
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-border bg-surface-muted px-1 py-0.5 font-mono">
              esc
            </kbd>
            Close
          </span>
          <span className="ml-auto text-content-subtle">
            {query && !loading
              ? `${allResults.length} result${allResults.length !== 1 ? 's' : ''}`
              : 'Type to search'}
          </span>
        </div>
      </div>
    </div>
  );
}
